// src/pages/care/ClaimResume.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import jwtDecode from "jwt-decode";
import { GET_TOKEN } from "../../../../platform/graphql/queries";
import { UPDATE_DEFAULT_BUSINESS } from "../../../../platform/graphql/mutations";
import { getToken, setToken } from "../../../../platform/util/authUtils";

const PENDING_KEY = "pendingClaim";
const PENDING_LOCK_KEY = "pendingClaim:lock";
const DASHBOARD_URL = "/biz/dashboard";
const AGREEMENT_CONTEXT_KEY = "pendingAgreementBusinessContext";
const getAgreementRedirectUrl = (businessId) =>
  `/care/agreement?businessId=${encodeURIComponent(
    String(businessId)
  )}&returnTo=${encodeURIComponent(DASHBOARD_URL)}`;

const CLAIM_AND_MAP = gql`
  mutation ClaimAndMap($input: ClaimAndMapInput!) {
    claimAndMapBusiness(input: $input) {
      success
      message
      token
    }
  }
`;

export default function ClaimResume() {
  const [err, setErr] = useState(null);
  const apolloClient = useApolloClient();
  const [claimAndMap] = useMutation(CLAIM_AND_MAP, { awaitRefetchQueries: true });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const run = async () => {
      try {
        const token = getToken();
        if (!token) {
          // no auth — send back to login, return to me
          window.location.assign(`/login?returnTo=${encodeURIComponent("/care/claim-resume")}`);
          return;
        }

        const raw = sessionStorage.getItem(PENDING_KEY);
        if (!raw) {
          // nothing to do, head to dashboard
          window.location.assign(DASHBOARD_URL);
          return;
        }

        if (sessionStorage.getItem(PENDING_LOCK_KEY) === "1") {
          // in-flight elsewhere; just head to dashboard
          window.location.assign(DASHBOARD_URL);
          return;
        }

        let pending;
        try {
          pending = JSON.parse(raw);
        } catch {
          sessionStorage.removeItem(PENDING_KEY);
          window.location.assign(DASHBOARD_URL);
          return;
        }

        const businessId = pending?.businessId ? Number(pending.businessId) : null;
        const licenseNumber = pending?.license || null;

        if (!businessId) {
          sessionStorage.removeItem(PENDING_KEY);
          window.location.assign(DASHBOARD_URL);
          return;
        }

        sessionStorage.setItem(PENDING_LOCK_KEY, "1");
        if (!sessionStorage.getItem(AGREEMENT_CONTEXT_KEY)) {
          try {
            const decodedCurrentToken = token ? jwtDecode(token) : null;
            sessionStorage.setItem(
              AGREEMENT_CONTEXT_KEY,
              JSON.stringify({
                defaultBusinessId: Number(decodedCurrentToken?.defaultBusinessId || 0),
                selectedBusinessId: Number(localStorage.getItem("selectedBusinessId") || 0),
              })
            );
          } catch (_err) {
            // ignore storage failures
          }
        }

        const { data } = await claimAndMap({
          variables: {
            input: {
              businessId,
              licenseNumber,
              treatAlreadyClaimedAsSuccess: true,
            },
          },
          // no need to refetch here, we fully reload the dashboard next
        });

        const payload = data?.claimAndMapBusiness;
        if (!payload?.success) {
          throw new Error(payload?.message || "Failed to claim & map");
        }
        const resolvedToken = payload?.token || token;
        if (payload?.token) setToken(payload.token);
        await updateDefaultBusiness({
          variables: { defaultBusinessId: Number(businessId) },
        });
        const decoded = resolvedToken ? jwtDecode(resolvedToken) : null;
        if (Number(decoded?.userId || 0) > 0) {
          const refreshedTokenResult = await apolloClient.query({
            query: GET_TOKEN,
            variables: {
              userId: Number(decoded.userId),
              businessId: Number(businessId),
              staffId: null,
              subscriptionId: null,
            },
            fetchPolicy: "network-only",
          });
          const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
          if (refreshedToken) {
            setToken(refreshedToken);
          }
        }

        // success — clear pending and go to dashboard (hard reload)
        sessionStorage.removeItem(PENDING_KEY);
        sessionStorage.removeItem(PENDING_LOCK_KEY);
        window.location.assign(getAgreementRedirectUrl(businessId));
      } catch (e) {
        // If token trouble, bounce to login and come back to me
        const msg = (e?.message || "").toLowerCase();
        if (msg.includes("unauth")) {
          window.location.assign(`/login?returnTo=${encodeURIComponent("/care/claim-resume")}`);
          return;
        }
        setErr(e?.message || "Unable to complete claim. Please try again.");
        // still route to dashboard so they have somewhere to land
        setTimeout(() => window.location.assign(DASHBOARD_URL), 1200);
      } finally {
        sessionStorage.removeItem(PENDING_LOCK_KEY);
      }
    };

    run();
  }, [apolloClient, claimAndMap, updateDefaultBusiness]);

  return (
    <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center", p: 3 }}>
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Finalizing your claim…
        </Typography>
        {err ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {err}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
